import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShopLayout } from "@/components/ShopLayout";
import { api, ApiError } from "@/lib/api";
import { cartTotal, clearCart, loadCart } from "@/lib/cart";
import { formatPrice } from "@/lib/format";

export function CheckoutPage() {
  const navigate = useNavigate();
  const lines = loadCart();
  const total = cartTotal(lines);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [checkoutEnabled, setCheckoutEnabled] = useState<boolean | null>(null);

  useEffect(() => {
    if (lines.length === 0) {
      navigate("/warenkorb", { replace: true });
    }
  }, [lines.length, navigate]);

  useEffect(() => {
    void api
      .get<{ checkout_enabled: boolean; stripe_publishable_key: string | null }>(
        "/api/v1/public/shop/config",
        { auth: false },
      )
      .then((cfg) => setCheckoutEnabled(cfg.checkout_enabled))
      .catch(() => setCheckoutEnabled(false));
  }, []);

  async function startCheckout() {
    setLoading(true);
    setError(null);
    const origin = window.location.origin;
    try {
      const res = await api.post<{
        checkout_id: string;
        checkout_url: string | null;
        total_amount: string;
      }>("/api/v1/public/shop/checkout/prepare", {
        lines: lines.map((line) => ({
          article_id: line.articleId,
          quantity: line.quantity,
          pickup_date: line.pickupDate,
        })),
        success_url: `${origin}/kasse/erfolg`,
        cancel_url: `${origin}/warenkorb`,
      });
      if (res.checkout_url) {
        clearCart();
        window.location.href = res.checkout_url;
        return;
      }
      setError("Checkout konnte nicht gestartet werden.");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Checkout fehlgeschlagen.");
    } finally {
      setLoading(false);
    }
  }

  if (lines.length === 0) return null;

  return (
    <ShopLayout>
      <div className="shop-page mx-auto max-w-2xl space-y-6">
        <header>
          <h1 className="shop-page-title">Kasse</h1>
          <p className="shop-page-subtitle">
            Sie werden zur sicheren Zahlung mit Stripe weitergeleitet.
          </p>
        </header>

        <div className="shop-card space-y-3">
          {lines.map((line) => (
            <div key={line.articleId} className="flex justify-between gap-4 text-sm">
              <span className="text-slate-700">
                <span className="font-semibold text-slate-900">{line.quantity}×</span> {line.name}
              </span>
              <span className="shrink-0 tabular-nums font-medium text-slate-900">
                {formatPrice(line.priceGross * line.quantity)}
              </span>
            </div>
          ))}
          <div className="flex justify-between border-t border-slate-100 pt-4">
            <span className="font-semibold text-slate-700">Gesamt</span>
            <span className="text-xl font-bold tabular-nums text-slate-900">{formatPrice(total)}</span>
          </div>
        </div>

        {checkoutEnabled === false ? (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            Stripe ist auf dem Server noch nicht konfiguriert. Setzen Sie{" "}
            <code className="rounded bg-amber-100 px-1 text-xs">STRIPE_SECRET_KEY</code> und{" "}
            <code className="rounded bg-amber-100 px-1 text-xs">STRIPE_PUBLISHABLE_KEY</code>.
          </div>
        ) : null}

        {error ? (
          <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        ) : null}

        <button
          type="button"
          disabled={loading || checkoutEnabled === false}
          onClick={() => void startCheckout()}
          className="shop-btn-primary w-full"
        >
          {loading ? "Weiterleitung…" : "Mit Stripe bezahlen"}
        </button>

        <Link to="/warenkorb" className="shop-btn-ghost mx-auto block w-full text-center">
          Zurück zum Warenkorb
        </Link>
      </div>
    </ShopLayout>
  );
}
